import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { api } from '@/api';
import { PERMISSION_OBJ_LOT } from '@/constants/permissions';
import { getAllowedFormFields } from '@/components/generic-form/utils';
import { DrawerForm } from '@/components/drawer-form';
import { lotStore, waybillStore } from '@/stores';
import { getTranslatedString } from '@/utils';

import { getFormFields } from './setup';
import { withoutRepeatValue } from '@/utils/controller';
import { useMassUpdateCashedStores } from '@/utils/store';
import { showError } from '@/utils/common';

const entity = 'waybills';

export const LotForm = observer(({ transition }: UIViewInjectedProps) => {
  const { waybillId } = transition.router.globals.params;
  const { loading } = useMassUpdateCashedStores([lotStore]);

  const [mostRecentLot, setMostRecentLot] = useState<cyrian.api.lots.Lot>();

  useEffect(() => {
    const getMostRecent = async () => {
      try {
        const result = await api.lots.getMostRecentLot().source;
        setMostRecentLot(result?.data);
      } catch (error) {
        showError(error);
      }
    };

    getMostRecent();
  }, []);

  const onClose = () => {
    transition.router.stateService.go(`base-layout.${entity}`);
  };

  const latestLotNumber = mostRecentLot?.number ?? 'N/A';

  const initValues = {};
  const title = getTranslatedString('waybills.lot.title', { 0: latestLotNumber });
  const formFields = getFormFields();
  const allowedFields = getAllowedFormFields(formFields, true, PERMISSION_OBJ_LOT);

  const resourceController = withoutRepeatValue(initValues, {
    create: async (values: any) => {
      const lotRequestData = {
        number: values.number,
        isDraft: true,
      };

      const result = await api.lots.create(lotRequestData).source;
      const createdLot = result?.data;
      if (!createdLot) return;

      lotStore.addItem(createdLot);

      const wbId = waybillId ? +waybillId : 0;
      if (wbId) {
        try {
          const linkRes = await api.waybills.addLots(wbId, [createdLot.id]).source;
          if (linkRes?.data) {
            waybillStore.updateItem(linkRes.data);
          }
        } catch (error) {
          showError(error);
        }
      }

      return createdLot;
    },
  });

  return (
    <DrawerForm
      title={title}
      initValues={initValues}
      formFields={allowedFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={loading}
    />
  );
});
